import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Gift, Copy, Check, Users } from 'lucide-react';
import { referralsAPI } from '../api';
import { toast } from 'sonner';

const ReferralsPanel = () => {
  const [info, setInfo] = useState(null);
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await referralsAPI.me();
      setInfo(res.data);
    } catch (e) {
      toast.error('Error cargando referidos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyCode = () => {
    if (!info?.code) return;
    navigator.clipboard.writeText(info.code);
    setCopied(true);
    toast.success('Código copiado');
    setTimeout(() => setCopied(false), 2000);
  };

  const share = () => {
    const url = `${window.location.origin}/signup?ref=${info.code}`;
    if (navigator.share) {
      navigator.share({ title: 'Mobility', text: `Renta autos con $200 MXN de descuento. Usa mi código: ${info.code}`, url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copiado');
    }
  };

  const redeem = async () => {
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      const res = await referralsAPI.redeem(code.trim().toUpperCase());
      toast.success(`+$${res.data.credited} MXN de crédito de ${res.data.referrer_name}`);
      setCode('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error redimiendo código');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <Card data-testid="referrals-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-purple-600" /> Programa de Referidos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Credit Balance */}
        <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
          <p className="text-sm text-gray-600">Crédito disponible</p>
          <p className="text-3xl font-bold text-purple-700" data-testid="credit-balance">
            ${(info?.credit_balance || 0).toLocaleString()} <span className="text-base font-normal text-gray-500">MXN</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">Se aplicará automáticamente en tu próxima reserva.</p>
        </div>

        {/* My Code */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">Tu código personal</label>
          <div className="flex gap-2">
            <code className="flex-1 px-4 py-3 bg-gray-900 text-white font-mono font-bold tracking-wide rounded-lg text-center text-lg" data-testid="my-referral-code">
              {info?.code}
            </code>
            <Button onClick={copyCode} variant="outline" size="icon" className="h-auto" data-testid="copy-code-btn">
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          <Button onClick={share} className="w-full mt-2 bg-purple-600 hover:bg-purple-700" data-testid="share-code-btn">
            Compartir código
          </Button>
        </div>

        <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
          <p className="font-medium text-blue-900 mb-1">¿Cómo funciona?</p>
          <ul className="space-y-1 text-xs">
            <li>• Tu amigo se registra y usa tu código</li>
            <li>• Él recibe <strong>$200 MXN</strong> en su primera reserva</li>
            <li>• Tú recibes <strong>$100 MXN</strong> de crédito</li>
          </ul>
        </div>

        {/* Redeem */}
        {!info?.referrals_count > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">¿Tienes un código? Redímelo</label>
            <div className="flex gap-2">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Ej: JUAN1RJ1"
                maxLength={12}
                className="font-mono uppercase"
                data-testid="redeem-code-input"
              />
              <Button onClick={redeem} disabled={!code.trim() || submitting} data-testid="redeem-btn">
                {submitting ? '...' : 'Redimir'}
              </Button>
            </div>
          </div>
        )}

        {/* Referrals list */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-sm flex items-center gap-1.5">
              <Users className="w-4 h-4" /> Tus referidos
            </h3>
            <Badge variant="outline" data-testid="referrals-count">{info?.referrals_count || 0}</Badge>
          </div>
          {info?.referrals?.length ? (
            <div className="space-y-2">
              {info.referrals.map((r, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm" data-testid={`referral-${i}`}>
                  <span>{r.name}</span>
                  <span className="text-xs text-green-600 font-medium">+$100 MXN</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500 italic">Aún no tienes referidos. ¡Comparte tu código!</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralsPanel;
